import React, { useCallback, useEffect, useRef, useState } from "react";
import "swiper/css";
import 'swiper/css/pagination';
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import "./BonusPage.scss";
import BONUS_API from "../../services/bonus";
import { toast } from "react-toastify";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import API from "../../services";
import { UserDetails } from "../../models/UserDetails";
import { RootState } from "../../models/RootState";
import { setUserDetails } from "../../store/authReducer";
import { connect } from "react-redux";
import BalnceCard from "./../../assets/images/balance-card.svg";
import { AiOutlineInfoCircle } from "react-icons/ai";

interface BalanceCardProps {
    userDetails: UserDetails;
    setUserDetails: (data: any) => void;
}

const BonusPage: React.FC<BalanceCardProps> = ({ userDetails }) => {
    const location = useLocation();
    const { tId, amount, curType, curCategory, status } = location.state || {};

    const HISTORY = "History"
    const CLAIM_BONUS = "Claim Bonus"
    const navigate = useNavigate();
    const navigationType = useNavigationType();
    const [depositAmount, setDepositAmount] = useState<number | null>(amount);
    const [transactionId, setTransactionId] = useState<string | null>(tId);
    const [currency, setCurrency] = useState<string | null>(curType);
    const [currencyCategory, setCurrencyCategory] = useState<string | null>(curCategory);
    const [redeemCount, setRedeemCount] = useState<number>(0);
    const [redeemAmount, setRedeemAmount] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [redeemLoading, setRedeemLoading] = useState<boolean>(false);
    const [hasMore, setHasMore] = useState(true);
    const [bonusData, setBonusData] = useState<any[]>([]);
    const [policyData, setPolicyData] = useState<any[]>([]);
    const [activeTab, setActiveTab] = useState<string>();
    const [nextPage, setNextPage] = useState<string>('');
    const [pageSize] = useState<number>(10);
    const observer = useRef<IntersectionObserver | null>(null);
    const datePickerRef = useRef<HTMLDivElement>(null);
    const [expandedIndexes, setExpandedIndexes] = useState<Set<string>>(new Set());
    const statusMap: Record<string, string> = {
        AWARDED: "Awarded",
        REDEEMABLE: "Redeemable",
        REDEEMED: "Redeemed",
        CANCELLED: "Cancelled",
        EXPIRED: "Expired"
    };
    const categoryMap: Record<string, string> = {
        ALL: "All",
        DEPOSIT_BONUS: "Deposit Bonus",
        LOSSBACK_BONUS: "Lossback Bonus",
    };

    const [bonusCategory, setBonusCategory] = useState<string>("");
    const [policyCategory, setPolicyCategory] = useState<string>("");
    const [bonusCode, setBonusCode] = useState<string>("");
    const [bonusStatus, setBonusStatus] = useState<string>(status ? status : "AWARDED");
    const [showInfo, setShowInfo] = useState(false);
    const infoRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        getuserStats();
        if (transactionId) {
            handleTabClick(CLAIM_BONUS);
        } else {
            handleTabClick(HISTORY);
        }
        if (location.state) {
            navigate(location.pathname, { replace: true });
        }
    }, []);

    const handleInfoClick = () => {
        setShowInfo(true);
        setTimeout(() => {
          setShowInfo(false);
        }, 5000); // Hide the info after 3 seconds
      };

      useEffect(() => {
        const handleClickOutside = (event:any) => {
          if (infoRef.current && !infoRef.current.contains(event.target)) {
            setShowInfo(false);
          }
        };
    
        if (showInfo) {
          document.addEventListener('mousedown', handleClickOutside);
        } else {
          document.removeEventListener('mousedown', handleClickOutside);
        }
    
        return () => {
          document.removeEventListener('mousedown', handleClickOutside);
        };
      }, [showInfo]);

    const handleBackClick = () => {
        if (navigationType === 'POP') {
            navigate('/home'); // Go to home
        } else {
            navigate(-1); // Go back
        }
    };

    const toggleAccordion = (index: string) => {
        setExpandedIndexes((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(index)) {
                newSet.delete(index);
            } else {
                newSet.add(index);
            }
            return new Set(newSet); // Create a new Set to trigger a re-render
        });
    };

    const dateFormat = (time: number) => {
        return new Date(time).toLocaleString("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
        });
    };

    const loadMoreBonuses = useCallback(async (reset = false) => {
        if (!reset && (loading || !hasMore)) return;
        setLoading(true);

        try {
            const params = {
                user_key: sessionStorage.getItem("userKey"),
                page_token: reset ? '' : nextPage,
                page_size: pageSize,
                status: bonusStatus === 'ALL' ? '' : bonusStatus,
                category: bonusCategory === 'ALL' ? '' : bonusCategory,
                sort: "asc",
            };

            const response = await BONUS_API.get('/bonuses', { params });
            const data = response?.data?.bonuses || [];

            if (reset) {
                setBonusData(data);
            } else {
                setBonusData(prev => [...prev, ...data]);
            }

            setHasMore(data.length === pageSize);
            setNextPage(response.data?.next_page_token || '');
        } catch (e) {
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    }, [loading, hasMore, nextPage, pageSize, bonusStatus, bonusCategory]);

    useEffect(() => {
        if (activeTab == HISTORY) {
            setBonusData([]);
            setNextPage('');
            setHasMore(true);
            loadMoreBonuses(true);
        }
    }, [bonusStatus, bonusCategory]);

    useEffect(() => {
        if (activeTab !== HISTORY && policyCategory !== "") {
            getEligibleBonuses("");
        }
    }, [policyCategory]);

    useEffect(() => {
        const handleIntersection = ([entry]: IntersectionObserverEntry[]) => {
            if (entry.isIntersecting && !loading && hasMore) {
                loadMoreBonuses();
            }
        };

        observer.current = new IntersectionObserver(handleIntersection, {
            root: null,
            rootMargin: "20px",
            threshold: 1.0
        });

        const sentinel = document.getElementById("sentinel");
        if (sentinel) observer.current.observe(sentinel);

        return () => {
            if (observer.current && sentinel) {
                observer.current.unobserve(sentinel);
            }
        };
    }, [loading, hasMore]);

    const handleTabClick = (tab: any) => {
        setActiveTab(tab);
        setBonusCode("");
        if (tab === HISTORY) {
            setDepositAmount(0);
            setTransactionId("");
            setCurrency("");
            setCurrencyCategory("");
            setExpandedIndexes(new Set());
            loadMoreBonuses(true);
        } else if (tab === CLAIM_BONUS) {
            getEligibleBonuses("");
        }
    };

    const getEligibleBonuses = async (code: string) => {
        if (loading) return;
        setLoading(true);
        let response: any;
        try {
            const params = {
                user_key: sessionStorage.getItem("userKey"),
                deposit_transaction_id: transactionId,
                category: transactionId ? "DEPOSIT_BONUS" : policyCategory === 'ALL' ? '' : policyCategory,
                type: transactionId ? "INSTANT" : "RETROSPECTIVE",
                code: code
            };
            response = await BONUS_API.post(`/bonus-policies/eligible`,
                params);

            setPolicyData(response?.data?.policies);

        } catch (e: any) {
            toast.error(e?.response?.data?.message);
            setLoading(false);
            return;
        }
        setLoading(false);

    };

    const getuserStats = async () => {
        let response: any;
        try {
            const params = {
                user_key: sessionStorage.getItem("userKey")
            };
            response = await BONUS_API.post(`/reports/user-bonus-metrics`,
                params);

            setRedeemCount(response?.data?.total_redeemed_count);
            setRedeemAmount(response?.data?.total_redeemed_amount);

        } catch (e: any) {
            toast.error(e?.response?.data?.message);
            return;
        }
    };

    const handleRedeem = async (bonus: any) => {
        if (redeemLoading) return;
        setRedeemLoading(true);
        let response: any;
        try {
            const params = {
                user_key: bonus?.user_key,
            };
            response = await BONUS_API.post(`/bonuses/${bonus?.id}/redeem`, {}, { params });
        } catch (e: any) {
            toast.error(e?.response?.data?.message);
            setRedeemLoading(false);
            return;
        }
        setRedeemLoading(false);

        if (response?.status == 204) {
            getuserStats();
            toast.success("You have successfully redeemed your bonus!");
        } else {
            toast.error(response?.data?.message);
        }
        loadMoreBonuses(true);
    };

    const handleClaim = async (policy: any) => {
        if (redeemLoading) return;
        setRedeemLoading(true);
        let response: any;
        try {
            const params = {
                user_key: sessionStorage.getItem("userKey"),
                deposit_transaction_id: transactionId,
            };
            response = await BONUS_API.post(`/bonus-policies/${policy?.id}/claim`, params);
        } catch (e: any) {
            toast.error(e?.response?.data?.message);
            setRedeemLoading(false);
            return;
        }
        setRedeemLoading(false);
        if (response?.status == 204) {
            setDepositAmount(0);
            setTransactionId("");
            setCurrency("");
            setCurrencyCategory("");
            handleTabClick(HISTORY)
            toast.success("You have successfully claimed your bonus!");
        } else {
            toast.error(response?.data?.message);
        }
    };

    const navtoTransactionDetails = async (referenceId: string) => {
        try {
            const userKey = sessionStorage.getItem("userKey");
            const res = await API.post("/account/get-transactions", {
                pageSize: 1,
                userKey: userKey,
                referenceId: referenceId
            });

            if (res.data.status === "RS_OK" && res?.data?.transactions?.length) {
                const transaction = res?.data?.transactions?.[0];
                if (transaction?.referenceId === referenceId) {
                    navigate("/transaction-history-details", { state: { details: transaction, previous:"bonus", status: bonusStatus } });
                }
            }
        } catch (error) {
        }
    }

    const tabs = [
        {
            label: CLAIM_BONUS,
            content: (
                <>
                    {!transactionId ? (
                        <>
                            <div className="filters flex flex-wrap gap-3 my-4">
                                <div className="w-[48%]">
                                    <select
                                        value={policyCategory}
                                        onChange={(e) => setPolicyCategory(e.target.value)}
                                        className="flex items-center justify-between w-full px-3 py-2 text-xs font-medium text-white whitespace-nowrap rounded-lg border border-solid bg-inputbox-color"
                                    >
                                        <option value="" disabled>Select Category</option>
                                        {Object.entries(categoryMap).map(([key, value]) => (
                                            <option key={key} value={key}>{value}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="search-section">
                                <h2>{'Redeem bonus with coupon code'}</h2>
                                <div className="search-bar">
                                    <input
                                        type="text"
                                        className="coupon-input"
                                        placeholder={'Enter Coupon Code'}
                                        value={bonusCode}
                                        onChange={(e) => setBonusCode(e.target.value)}
                                    />
                                    <button
                                        className="search-button"
                                        onClick={() => getEligibleBonuses(bonusCode)}
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                                        ) : (
                                            "Search"
                                        )}
                                    </button>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="search-section gap-0 grid-gap-0 bg-gray-100 p-4 rounded-lg shadow">
                            <div className="text-lg font-semibold text-border-color">Transaction Details</div>
                            <div className="text-sm text-white">
                                <p><strong>Amount:</strong> {depositAmount}</p>
                                <p><strong>Currency:</strong> {currency}</p>
                            </div>
                            <div className="text-xs text-red-500 font-medium">
                                Disclaimer: You can only claim one of the available bonuses.
                            </div>
                        </div>
                    )}

                    <div className="relative">
                        {policyData?.length > 0 ? (
                            policyData?.map((row, index) => (
                                <div key={index} className="bonus-card flex flex-col gap-2 px-4 py-2 mb-2 w-full rounded-lg bg-meitms-color">
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <div className="text-border-color">
                                                {categoryMap[row?.category] || row?.category}
                                            </div>
                                            <div className="mt-1">{row?.name}</div>
                                        </div>
                                        <button
                                            className="status bg-border-color text-white px-3 py-1 rounded cursor-pointer"
                                            onClick={() => handleClaim(row)}
                                        >
                                            Claim
                                        </button>
                                    </div>
                                    <div className="expiry text-sm text-gray-400">
                                        {row?.description}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center text-gray-400 pt-8">No Eligible Bonuses</div>
                        )}

                        {redeemLoading && (
                            <div className="fixed inset-0 loading-spinner flex items-center justify-center py-4">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                            </div>
                        )}
                    </div>
                </>
            )
        },
        {
            label: HISTORY,
            content: (
                <>
                    <div className="filters flex flex-wrap gap-3 my-4">
                        <div className="w-[48%]">
                            <select
                                value={bonusStatus}
                                onChange={(e) => setBonusStatus(e.target.value)}
                                className="flex items-center justify-between w-full px-3 py-2 text-xs font-medium text-white whitespace-nowrap rounded-lg border border-solid bg-inputbox-color"
                            >
                                <option value="" disabled>Select Status</option>
                                {Object.entries(statusMap).map(([key, value]) => (
                                    <option key={key} value={key}>{value}</option>
                                ))}
                            </select>
                        </div>
                        {/* <div className="w-[48%]">
                            <select
                                value={bonusCategory}
                                onChange={(e) => setBonusCategory(e.target.value)}
                                className="flex items-center justify-between w-full px-3 py-2 text-xs font-medium text-white whitespace-nowrap rounded-lg border border-solid bg-inputbox-color"
                            >
                                <option value="" disabled>Select Category</option>
                                {Object.entries(categoryMap).map(([key, value]) => (
                                    <option key={key} value={key}>{value}</option>
                                ))}
                            </select>
                        </div> */}
                    </div>

                    <div className="flex justify-center mb-2">
                        <div className="mr-16">
                            <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mr-2"></span>Points
                        </div>
                        <div>
                            <span className="inline-block w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>Play Chips
                        </div>
                    </div>

                    <div className="relative">
                        {bonusData?.map((row, index) => (
                            <div
                                key={index}
                                className="bonus-card flex flex-col gap-2 px-4 py-2 mb-2 w-full rounded-lg bg-meitms-color transition-all duration-300"
                            >
                                <div className="flex justify-between items-center cursor-pointer">
                                    <div className="text-border-color">{categoryMap[row?.category]}</div>

                                    <div className="flex items-center gap-2 ml-auto">
                                        <button
                                            className={`status px-3 py-1 rounded cursor-pointer ${row.status === "REDEEMABLE"
                                                ? "bg-border-color text-white"
                                                : "bg-gray-400 text-white cursor-not-allowed"
                                                }`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (row.status === "REDEEMABLE") {
                                                    handleRedeem(row);
                                                }
                                            }}
                                            disabled={row.status !== "REDEEMABLE"}
                                        >
                                            {row.status === "REDEEMABLE" ? "Redeem" : statusMap[row.status]}
                                        </button>

                                    </div>

                                </div>

                                <div className="flex justify-between mb-1">
                                    <div>
                                        Amount:{" "}
                                        <span
                                            className={`inline-block w-2 h-2 rounded-full mr-1 ${row?.reward_type === "POINTS"
                                                ? "bg-blue-500"
                                                : row?.reward_type === "PLAY_CHIPS"
                                                    ? "bg-yellow-500"
                                                    : ""
                                                }`}
                                        ></span>
                                        {row?.award_amount}
                                    </div>

                                    <span className="flex items-center justify-center w-6 h-6 text-gray-400 text-lg" onClick={() => toggleAccordion(row?.id)}>
                                        {expandedIndexes.has(row?.id) ? <ExpandLess /> : <ExpandMore />}
                                    </span>
                                </div>

                                {expandedIndexes.has(row?.id) && (
                                    <div className="mt-2 p-2 border-t border-gray-500 text-sm text-gray-300">
                                        <div>
                                            <strong>Target Turnover: {" "}</strong>
                                            {row?.status === "AWARDED"
                                                ? userDetails?.Wager + bonusData.slice(0, index + 1).reduce((acc, item) => acc + (item?.turnover_required - item?.turnover_met), 0)
                                                : (row?.status === "EXPIRED" || row?.status === "CANCELLED")
                                                    ? "N/A"
                                                    : (row?.status === "REDEEMABLE" || row?.status === "REDEEMED")
                                                        ? "Reached"
                                                        : null}
                                        </div>

                                        {/* Progress Bar with Conditional Styling */}
                                        <div className="mt-2 mb-2">
                                            <div className="flex items-center w-full">
                                                <div className="w-full bg-gray-700 rounded-full h-4 overflow-hidden relative">
                                                    <div
                                                        className={`h-full transition-all duration-300 ${row?.status === "CANCELLED" || row?.status === "EXPIRED"
                                                            ? "bg-red-500"
                                                            : "bg-green-500"
                                                            }`}
                                                        style={{
                                                            width: `${row?.turnover_required === 0
                                                                ? 100
                                                                : Math.max(0, Math.min((row?.turnover_met / row?.turnover_required) * 100, 100))
                                                                }%`
                                                        }}
                                                    ></div>
                                                </div>
                                                <span className="ml-2 text-xs text-gray-300 whitespace-nowrap">
                                                    {`${row?.turnover_required === 0
                                                            ? 100
                                                            : (Math.max(0, Math.min((row?.turnover_met / row?.turnover_required) * 100, 100))).toFixed(2)
                                                        }%`}
                                                </span>
                                            </div>
                                        </div>

                                        {
                                            (row?.status === "AWARDED" || row?.status === "EXPIRED") && (
                                                <div className="relative" ref={infoRef}>
                                                    <div className="flex justify-between items-start">
                                                        <div className="text-white">
                                                        <strong>Expires:</strong> {dateFormat(row?.expiry_time)}
                                                        </div>
                                                        <AiOutlineInfoCircle
                                                        className="text-white text-lg cursor-pointer ml-2"
                                                        onClick={handleInfoClick}
                                                        />
                                                    </div>

                                                    {showInfo && (
                                                        <div className="absolute right-0 mt-1 z-10 w-72 p-2 text-sm text-white bg-gray-700 border border-gray-500 rounded-lg shadow-md">
                                                        The bonus will expire if the required turnover is not achieved before the expiry time.
                                                        </div>
                                                    )}
                                                    </div>
                                            )
                                        }
                                        {row?.reference_id && (
                                            <div>
                                                <strong>ReferenceId: </strong>
                                                <span className="underline cursor-pointer" onClick={() => navtoTransactionDetails(row?.reference_id)}>
                                                    {row?.reference_id}
                                                </span>
                                            </div>
                                        )}
                                        {row?.notes && (
                                            <div><strong>Note:</strong> {row.notes}</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}

                        {redeemLoading && (
                            <div className="fixed inset-0 loading-spinner flex items-center justify-center py-4">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                            </div>
                        )}
                    </div>

                    {loading && (
                        <div className="loading-spinner flex justify-center py-4">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                        </div>
                    )}
                    <div id="sentinel" className="h-4" />
                </>
            )
        },
    ];

    return (
        <div className="bonus-page flex flex-col w-full pb-[7rem] py-[1rem]">
            {/* <div className="flex gap-4 w-full mb-4 text-center">
                <div className="flex flex-col bg-inputbox-color p-2 rounded-lg w-full text-white">
                    <span className="text-sm text-gray-300"> <span>Total Wager: {" "}</span>
                        <span className="text-lg font-semibold">{userDetails?.Wager}</span> </span>
                </div>
            </div>
            <div className="flex gap-4 w-full mb-4 text-center">
                <div className="flex flex-col bg-inputbox-color p-2 rounded-lg w-1/2 text-white">
                    <span className="text-sm text-gray-300">Redeem Count</span>
                    <span className="text-lg font-semibold">{redeemCount}</span>
                </div>
                <div className="flex flex-col bg-inputbox-color p-2 rounded-lg w-1/2 text-white">
                    <span className="text-sm text-gray-300">Redeem Amount</span>
                    <span className="text-lg font-semibold">{redeemAmount}</span>
                </div>
            </div> */}

            <section className="flex flex-col justify-center items-center max-w-[388px]">
                <div className="flex relative flex-col p-4 pb-2 w-full items-center">
                    <img
                        loading="lazy"
                        src={BalnceCard}
                        alt=""
                        className="object-cover absolute inset-0 size-full max-md:min-w-[92vw] md:min-w-[28rem]"
                    />
                    <div className="flex relative flex-col items-center text-center">
                        <div className="text-xs text-zinc-400">Total Wager</div>
                        <div className="flex items-center">
                            <div className="text-lg font-semibold text-white">{userDetails?.Wager}</div>
                        </div>
                    </div>
                    <div className="flex relative justify-between w-full">
                        <div>
                            <div className="text-xs text-zinc-400">Redeem Count</div>
                            <div className="flex items-center">
                                <div className="text-lg font-semibold text-white">{redeemCount}</div>
                            </div>
                        </div>
                        <div>
                            <div className="text-xs text-zinc-400">Redeem Amount</div>
                            <div className="flex items-center">
                                <div className="text-lg font-semibold text-white">{redeemAmount}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <div className="bonus-tabs">
                <div className="bonus-nav">
                    <div className="navi navi-pills" role="tablist">
                        {tabs.map((tab) => (
                            <div className="navi-item" key={tab.label}>
                                <a
                                    role="tab"
                                    className={`navi-link ${activeTab === tab.label ? "active" : ""}`}
                                    onClick={() => handleTabClick(tab.label)}
                                >
                                    {tab.label}
                                </a>
                            </div>
                        ))}
                    </div>

                    <div className="tab-content">
                        {tabs.map((tab) => (
                            <div
                                key={tab.label}
                                className={`tab-pane fade ${activeTab === tab.label ? "show active" : ""}`}
                                id={`tabs-tabpane-${tab.label}`}
                                role="tabpanel"
                            >
                                {activeTab === tab.label && tab.content}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = (state: RootState) => {
    return {
        userDetails: state.userDetails?.userDetails,
    };
};

const mapDispatchToProps = (dispatch: Function) => {
    return {
        setUserDetails: (data: any) => {
            dispatch(setUserDetails(data));
        },
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(BonusPage);
